import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { LoadingScreen } from '@/components/LoadingScreen'
import { Select } from '@/components/Select'
import {
  useDeletePoule,
  useCommitPouleMemberImport,
  usePouleAdminCommunicationPreview,
  usePouleAdminCommunicationSend,
  usePouleAdminCorrections,
  usePouleAdminAudit,
  usePouleAdminCommunications,
  usePouleAdminInvites,
  usePouleAdminMemberAction,
  usePouleAdminMemberRole,
  usePouleAdminMembers,
  usePouleAdminOverview,
  usePouleAdminSettings,
  usePreviewPouleMemberImport,
  useUpdatePouleAdminCorrection,
  useUpdatePouleAdminSettings,
} from '@/features/poules/adminApi'
import { useTranslation } from '@/i18n'

type MemberStatus = 'all' | 'accepted' | 'pending' | 'rejected'
type AdminTab = 'overview' | 'members' | 'communication' | 'imports' | 'corrections' | 'settings' | 'activity'

export function PouleAdminPage() {
  const { t, locale } = useTranslation()
  const navigate = useNavigate()
  const { pouleId } = useParams()
  const numericPouleId = Number(pouleId)
  const validPouleId = Number.isInteger(numericPouleId) && numericPouleId > 0 ? numericPouleId : null
  const [activeTab, setActiveTab] = useState<AdminTab>('overview')
  const [status, setStatus] = useState<MemberStatus>('accepted')
  const [query, setQuery] = useState('')
  const [licenseDialogOpen, setLicenseDialogOpen] = useState(false)
  const overviewQuery = usePouleAdminOverview(validPouleId)
  const requiresLicense = overviewQuery.data?.license.required === true
  const hasActiveLicense = !requiresLicense || overviewQuery.data?.license.active === true
  const membersQuery = usePouleAdminMembers(validPouleId, status, query, hasActiveLicense)
  const pendingMembersQuery = usePouleAdminMembers(validPouleId, 'pending', '', hasActiveLicense)
  const memberAction = usePouleAdminMemberAction(validPouleId)
  const memberRoleAction = usePouleAdminMemberRole(validPouleId)
  const invites = usePouleAdminInvites(validPouleId, hasActiveLicense)
  const communicationsQuery = usePouleAdminCommunications(validPouleId, overviewQuery.data?.permissions.can_manage_communication === true)
  const auditQuery = usePouleAdminAudit(validPouleId, hasActiveLicense)
  const settingsQuery = usePouleAdminSettings(validPouleId)
  const updateSettings = useUpdatePouleAdminSettings(validPouleId)
  const deletePoule = useDeletePoule(validPouleId)
  const previewImport = usePreviewPouleMemberImport(validPouleId)
  const commitImport = useCommitPouleMemberImport(validPouleId)
  const communicationPreview = usePouleAdminCommunicationPreview(validPouleId)
  const communicationSend = usePouleAdminCommunicationSend(validPouleId)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [messageSent, setMessageSent] = useState(false)
  const [correctionUserId, setCorrectionUserId] = useState<number | null>(null)
  const correctionsQuery = usePouleAdminCorrections(validPouleId, correctionUserId, hasActiveLicense)
  const updateCorrection = useUpdatePouleAdminCorrection(validPouleId, correctionUserId)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const members = membersQuery.data?.pages.flatMap((page) => page.data) ?? []
  const pendingMembers = pendingMembersQuery.data?.pages.flatMap((page) => page.data) ?? []

  useEffect(() => {
    const target = loadMoreRef.current
    if (!target || !membersQuery.hasNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !membersQuery.isFetchingNextPage) {
          void membersQuery.fetchNextPage()
        }
      },
      { rootMargin: '240px' },
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [membersQuery.fetchNextPage, membersQuery.hasNextPage, membersQuery.isFetchingNextPage])

  if (!validPouleId) {
    return <AdminState title={t('Ongeldige poule')} body={t('De URL bevat geen geldig poule-id.')} />
  }

  if (overviewQuery.isLoading) {
    return (
      <div className="space-y-6">
        <LoadingScreen compact />
        <div className="h-40 animate-pulse rounded-[1.5rem] bg-[#0d1113]" />
      </div>
    )
  }

  if (overviewQuery.isError || !overviewQuery.data) {
    const isForbidden = overviewQuery.error instanceof ApiError && overviewQuery.error.status === 403
    return (
      <AdminState
        title={isForbidden ? t('Geen beheerrechten') : t('Poulebeheer niet geladen')}
        body={isForbidden ? t('Je hebt geen beheerrechten voor deze poule.') : t('De beheerinformatie kon niet worden opgehaald.')}
        retry={!isForbidden ? () => void overviewQuery.refetch() : undefined}
      />
    )
  }

  const { poule, summary, membership, permissions, license } = overviewQuery.data

  async function handleMemberAction(userId: number, action: 'approve' | 'decline' | 'remove') {
    if (action === 'approve' && !hasActiveLicense) {
      openLicenseDialog()
      return
    }
    if (action === 'remove' && !window.confirm(t('Weet je zeker dat je dit lid wilt verwijderen?'))) return
    await memberAction.mutateAsync({ userId, action })
  }

  async function handleRoleChange(userId: number, role: 'member' | 'admin') {
    if (!hasActiveLicense) {
      openLicenseDialog()
      return
    }
    if (!window.confirm(t(role === 'admin' ? 'Weet je zeker dat je deze deelnemer beheerder wilt maken?' : 'Weet je zeker dat je deze beheerder wilt terugzetten naar deelnemer?'))) return
    await memberRoleAction.mutateAsync({ userId, role })
  }

  async function handleDeletePoule() {
    if (!permissions.can_delete) return
    if (!window.confirm(t('Weet je zeker dat je deze poule definitief wilt verwijderen?'))) return

    try {
      await deletePoule.mutateAsync()
      navigate('/dashboard/poules')
    } catch {
      // The mutation error is rendered in the settings card.
    }
  }

  function openLicenseDialog() {
    setLicenseDialogOpen(true)
  }

  const tabs: Array<{ key: AdminTab; label: string }> = [
    { key: 'overview', label: t('Overzicht') },
    { key: 'members', label: t('Deelnemers') },
    { key: 'communication', label: t('Communicatie') },
    { key: 'imports', label: t('Importeren') },
    { key: 'corrections', label: t('Correcties') },
    { key: 'settings', label: t('Instellingen') },
    { key: 'activity', label: t('Activiteit') },
  ]

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to={`/poules/${validPouleId}`} className="text-sm font-semibold text-surface-400 transition hover:text-white">
            ← {t('Terug naar poule')}
          </Link>
          <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.2em] text-accent-200">{t('Beheer')}</p>
          <h1 className="mt-1 text-2xl font-black tracking-tight text-white sm:text-3xl">{poule.name}</h1>
        </div>
        {permissions.can_manage_owner && license.required ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {!license.active ? (
              <Link
                to={'/poules/' + validPouleId + '/licentie'}
                className="inline-flex shrink-0 rounded-xl bg-accent-600 px-3.5 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500"
              >
                {t('Licentie kiezen')}
              </Link>
            ) : (
              <>
                <span className="text-xs font-semibold text-surface-400">
                  {t('Huidig pakket: {product}', { product: getCompanyPlanLabel(license.product_key, t) })}
                </span>
                {license.product_key !== 'company_unlimited' ? (
                  <Link
                    to={'/poules/' + validPouleId + '/licentie'}
                    className="inline-flex shrink-0 rounded-xl bg-accent-600 px-3.5 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500"
                  >
                    {t('Upgrade nu')}
                  </Link>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </header>

      {license.required && !license.active ? (
        <section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-amber-300/20 bg-amber-300/[0.06] p-4 sm:p-5">
          <div>
            <p className="text-sm font-bold text-amber-100">{t('Deze bedrijfspoule is nog niet geactiveerd.')}</p>
            <p className="mt-1 text-sm leading-6 text-amber-100/70">{t('Kies eerst een licentie om deelnemers uit te nodigen en de bedrijfsfuncties te gebruiken.')}</p>
          </div>
          <Link to={'/poules/' + validPouleId + '/licentie'} className="inline-flex shrink-0 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500">{t('Licentie kiezen')}</Link>
        </section>
      ) : null}

      <nav role="tablist" aria-label={t('Beheeronderdelen')} className="-mx-1 flex gap-1 overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#0d1113] p-1 scrollbar-none">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={`poule-admin-tab-${tab.key}`}
            aria-selected={activeTab === tab.key}
            aria-controls={`poule-admin-panel-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`shrink-0 rounded-xl px-3.5 py-2.5 text-sm font-bold transition sm:px-4 ${activeTab === tab.key ? 'bg-white/[0.1] text-white shadow-sm' : 'text-surface-400 hover:bg-white/[0.05] hover:text-surface-100'}`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'overview' ? (
        <section id="poule-admin-panel-overview" role="tabpanel" aria-labelledby="poule-admin-tab-overview" className="space-y-6">
          <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <AdminStat label={t('Leden')} value={summary.accepted_members_count} />
            <AdminStat label={t('Aanvragen')} value={summary.pending_members_count} />
            <AdminStat label={t('Beheerders')} value={summary.admins_count} />
            <AdminStat label={t('Actieve invites')} value={summary.active_invites_count} />
          </section>

          <section className={`rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6 ${!hasActiveLicense ? 'opacity-75' : ''}`} aria-disabled={!hasActiveLicense}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Aanvragen')}</p>
                  <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Wachten op goedkeuring')}</h2>
                  <p className="mt-2 text-sm leading-6 text-surface-400">{t('Deze mensen willen meedoen met je poule.')}</p>
                </div>
                <button type="button" disabled={!hasActiveLicense} onClick={() => setActiveTab('members')} className="rounded-xl border border-white/[0.12] px-3.5 py-2.5 text-sm font-bold text-surface-100 transition hover:bg-white/[0.07] disabled:cursor-not-allowed disabled:opacity-40">{t('Alle aanvragen bekijken')}</button>
              </div>
              {!hasActiveLicense ? <p className="mt-5 text-sm text-surface-500">{t('Activeer eerst een licentie om deelnemers en aanvragen te beheren.')}</p> : pendingMembersQuery.isLoading ? <p className="mt-5 text-sm text-surface-500">{t('Aanvragen laden...')}</p> : pendingMembersQuery.isError ? <p className="mt-5 text-sm text-red-300">{t('Deelnemers konden niet worden geladen.')}</p> : pendingMembers.length === 0 ? <p className="mt-5 text-sm text-surface-500">{t('Geen openstaande aanvragen.')}</p> : (
                <div className="mt-5 divide-y divide-white/[0.07] border-y border-white/[0.07]">
                  {pendingMembers.slice(0, 5).map((member) => <AdminMemberRow key={`${member.user_id}-${member.status}`} member={member} locale={locale} t={t} isBusy={memberAction.isPending} isRoleBusy={memberRoleAction.isPending} onAction={handleMemberAction} onRoleChange={handleRoleChange} />)}
                </div>
              )}
              {pendingMembers.length > 5 ? <p className="mt-3 text-xs text-surface-500">{t('{count} aanvragen in totaal.', { count: summary.pending_members_count })}</p> : null}
          </section>
        </section>
      ) : null}

      {activeTab === 'members' ? <section id="poule-admin-panel-members" role="tabpanel" aria-labelledby="poule-admin-tab-members" className="space-y-6">
      <InviteCard
        t={t}
        invites={invites.query.data?.data ?? []}
        isLocked={!hasActiveLicense}
        isLoading={invites.query.isLoading}
        isCreating={invites.create.isPending}
        isRevoking={invites.revoke.isPending}
        error={invites.query.error?.message ?? invites.create.error?.message ?? invites.revoke.error?.message ?? null}
        onCreate={() => (hasActiveLicense ? invites.create.mutate({}) : openLicenseDialog())}
        onRevoke={(inviteId) => invites.revoke.mutate(inviteId)}
      />

      <section className={`rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6 ${!hasActiveLicense ? 'opacity-75' : ''}`} aria-disabled={!hasActiveLicense}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Beheer')}</p>
            <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Deelnemers')}</h2>
          </div>
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <label className="sr-only" htmlFor="poule-admin-member-search">{t('Zoek deelnemers')}</label>
            <input
              id="poule-admin-member-search"
              type="search"
              value={query}
              disabled={!hasActiveLicense}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('Zoek deelnemers...')}
              className="min-w-0 flex-1 rounded-xl border border-white/[0.08] bg-[#111719] px-3 py-2.5 text-sm font-semibold text-white outline-none placeholder:text-surface-500 focus:border-accent-500/60 sm:w-56 sm:flex-none"
            />
            <label className="sr-only" htmlFor="poule-admin-member-status">{t('Filter op status')}</label>
            <div className="min-w-0 flex-1 sm:w-52 sm:flex-none">
              <Select
                value={status}
                disabled={!hasActiveLicense}
                onChange={(value) => setStatus(value as MemberStatus)}
                options={[
                  { value: 'accepted', label: t('Geaccepteerd') },
                  { value: 'pending', label: t('In afwachting') },
                  { value: 'rejected', label: t('Afgewezen') },
                  { value: 'all', label: t('Alle statussen') },
                ]}
                placeholder={t('Filter op status')}
                ariaLabel={t('Filter op status')}
              />
            </div>
          </div>
        </div>

        {!hasActiveLicense ? (
          <p className="mt-5 text-sm text-surface-500">{t('Activeer eerst een licentie om deelnemers en aanvragen te beheren.')}</p>
        ) : membersQuery.isLoading && !membersQuery.data ? (
          <p className="mt-5 text-sm text-surface-500">{t('Leden laden...')}</p>
        ) : membersQuery.isError ? (
          <p className="mt-5 text-sm text-red-300">{t('Deelnemers konden niet worden geladen.')}</p>
        ) : members.length === 0 ? (
          <p className="mt-5 text-sm text-surface-500">{t('Geen deelnemers gevonden.')}</p>
        ) : (
          <div className="mt-5 divide-y divide-white/[0.07] border-y border-white/[0.07]">
            {members.map((member) => (
              <AdminMemberRow
                key={`${member.user_id}-${member.status}`}
                member={member}
                locale={locale}
                t={t}
                isBusy={memberAction.isPending}
                isRoleBusy={memberRoleAction.isPending}
                onAction={handleMemberAction}
                onRoleChange={handleRoleChange}
              />
            ))}
          </div>
        )}

        {membersQuery.hasNextPage ? (
          <div ref={loadMoreRef} className="flex min-h-12 items-center justify-center pt-3 text-xs text-surface-500">
            {membersQuery.isFetchingNextPage ? t('Meer deelnemers laden...') : null}
          </div>
        ) : null}
        {memberAction.isError ? <p className="mt-3 text-sm text-red-300">{t(memberAction.error.message)}</p> : null}
      </section>
      </section> : null}

      {activeTab === 'communication' ? <section id="poule-admin-panel-communication" role="tabpanel" aria-labelledby="poule-admin-tab-communication" className="space-y-6">
      <CommunicationCard
        t={t}
        isLocked={!permissions.can_manage_communication}
        lockMessage={license.active ? t('Deze functie is alleen beschikbaar met het Ultimate-pakket.') : t('Activeer eerst een licentie om communicatie te gebruiken.')}
        subject={subject}
        body={body}
        preview={communicationPreview.data}
        isPreviewing={communicationPreview.isPending}
        isSending={communicationSend.isPending}
        messageSent={messageSent}
        error={communicationPreview.error?.message ?? communicationSend.error?.message ?? null}
        onSubjectChange={(value) => {
          setMessageSent(false)
          communicationPreview.reset()
          setSubject(value)
        }}
        onBodyChange={(value) => {
          setMessageSent(false)
          communicationPreview.reset()
          setBody(value)
        }}
        onPreview={() => {
          setMessageSent(false)
          communicationPreview.mutate({ subject, body, locale })
        }}
        onSend={() => {
          if (!communicationPreview.data || !window.confirm(t('Weet je zeker dat je dit bericht naar de deelnemers wilt versturen?'))) return
          communicationSend.mutate(
            { subject, body, locale, idempotency_key: createIdempotencyKey() },
            {
              onSuccess: () => {
                setMessageSent(true)
                setSubject('')
                setBody('')
                communicationPreview.reset()
              },
            },
          )
        }}
      />
      <CommunicationHistory t={t} locale={locale} communications={communicationsQuery.data?.data ?? []} isLoading={communicationsQuery.isLoading} />
      </section> : null}

      {activeTab === 'imports' ? <section id="poule-admin-panel-imports" role="tabpanel" aria-labelledby="poule-admin-tab-imports">
      <ImportCard
        t={t}
        isLocked={!permissions.can_manage_imports}
        lockMessage={license.active ? t('Deze functie is alleen beschikbaar met het Ultimate-pakket.') : t('Activeer eerst een licentie om CSV-import te gebruiken.')}
        importPreview={previewImport.data}
        isPreviewing={previewImport.isPending}
        isCommitting={commitImport.isPending}
        error={previewImport.error?.message ?? commitImport.error?.message ?? null}
        onFileSelected={(file) => previewImport.mutate(file)}
        onCommit={() => {
          if (!previewImport.data || !window.confirm(t('Weet je zeker dat je deze import wilt bevestigen?'))) return
          commitImport.mutate({ importId: previewImport.data.id, idempotencyKey: createIdempotencyKey() })
        }}
      />
      </section> : null}

      {activeTab === 'corrections' ? <section id="poule-admin-panel-corrections" role="tabpanel" aria-labelledby="poule-admin-tab-corrections">
      <CorrectionCard
        t={t}
        isLocked={!hasActiveLicense}
        locale={locale}
        members={members.filter((member) => member.status === 'accepted')}
        selectedUserId={correctionUserId}
        corrections={correctionsQuery.data?.data ?? []}
        isLoading={correctionsQuery.isLoading}
        isSaving={updateCorrection.isPending}
        error={correctionsQuery.error?.message ?? updateCorrection.error?.message ?? null}
        onSelectUser={setCorrectionUserId}
        onSave={(matchId, homePred, awayPred) => updateCorrection.mutate({ matchId, homePred, awayPred })}
      />
      </section> : null}

      {activeTab === 'settings' ? <section id="poule-admin-panel-settings" role="tabpanel" aria-labelledby="poule-admin-tab-settings">
      <SettingsCard
        t={t}
        settings={settingsQuery.data}
        isLoading={settingsQuery.isLoading}
        isSaving={updateSettings.isPending}
        isLocked={!hasActiveLicense}
        error={settingsQuery.error?.message ?? updateSettings.error?.message ?? null}
        onSave={(payload) => updateSettings.mutate(payload)}
        isOwner={membership.role === 'owner'}
        canDelete={permissions.can_delete}
        isDeleting={deletePoule.isPending}
        deleteError={deletePoule.error?.message ?? null}
        onDelete={() => void handleDeletePoule()}
      />
      </section> : null}

      {activeTab === 'activity' ? <section id="poule-admin-panel-activity" role="tabpanel" aria-labelledby="poule-admin-tab-activity">
      <AuditCard t={t} locale={locale} events={auditQuery.data?.data ?? []} isLoading={auditQuery.isLoading} />
      </section> : null}

      <LicenseRequiredDialog
        t={t}
        open={licenseDialogOpen}
        onClose={() => setLicenseDialogOpen(false)}
        pouleId={validPouleId}
      />
    </div>
  )
}

function CommunicationCard({
  t,
  isLocked,
  lockMessage,
  subject,
  body,
  preview,
  isPreviewing,
  isSending,
  messageSent,
  error,
  onSubjectChange,
  onBodyChange,
  onPreview,
  onSend,
}: {
  t: (key: string, values?: Record<string, string | number>) => string
  isLocked: boolean
  lockMessage: string
  subject: string
  body: string
  preview: import('@/features/poules/adminApi').PouleAdminCommunicationPreview | undefined
  isPreviewing: boolean
  isSending: boolean
  messageSent: boolean
  error: string | null
  onSubjectChange: (value: string) => void
  onBodyChange: (value: string) => void
  onPreview: () => void
  onSend: () => void
}) {
  const canPreview = !isLocked && subject.trim() !== '' && body.trim() !== '' && !isPreviewing && !isSending

  return (
    <section className={`rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6 ${isLocked ? 'opacity-75' : ''}`} aria-disabled={isLocked}>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Communicatie')}</p>
        <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Bericht sturen')}</h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-400">{t('Stuur een e-mail naar alle geaccepteerde deelnemers. De begroeting en afsluiting worden automatisch één keer toegevoegd.')}</p>
        {isLocked ? <p className="mt-3 rounded-xl border border-amber-300/20 bg-amber-300/[0.06] px-3 py-2 text-sm text-amber-100/80">{lockMessage}</p> : null}
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-3">
          <div>
            <label htmlFor="poule-admin-mail-subject" className="mb-1.5 block text-sm font-semibold text-surface-200">{t('Onderwerp')}</label>
            <input
              id="poule-admin-mail-subject"
              value={subject}
              disabled={isLocked}
              onChange={(event) => onSubjectChange(event.target.value)}
              maxLength={180}
              placeholder={t('Bijvoorbeeld: klaar voor de volgende speelronde')}
              className="w-full rounded-xl border border-white/[0.08] bg-[#111719] px-3 py-2.5 text-sm font-semibold text-white outline-none placeholder:text-surface-500 focus:border-accent-500/60"
            />
          </div>
          <div>
            <label htmlFor="poule-admin-mail-body" className="mb-1.5 block text-sm font-semibold text-surface-200">{t('Bericht')}</label>
            <textarea
              id="poule-admin-mail-body"
              value={body}
              disabled={isLocked}
              onChange={(event) => onBodyChange(event.target.value)}
              maxLength={10000}
              rows={7}
              placeholder={t('Schrijf hier alleen de inhoud van je bericht.')}
              className="w-full resize-y rounded-xl border border-white/[0.08] bg-[#111719] px-3 py-2.5 text-sm leading-6 text-white outline-none placeholder:text-surface-500 focus:border-accent-500/60"
            />
            <p className="mt-1.5 text-xs text-surface-500">{t('De mail begint met een persoonlijke begroeting en eindigt met een vaste afsluiting.')}</p>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button type="button" disabled={!canPreview} onClick={onPreview} className="rounded-xl border border-white/[0.12] px-4 py-2.5 text-sm font-bold text-surface-100 transition hover:bg-white/[0.07] disabled:cursor-not-allowed disabled:opacity-40">{isPreviewing ? t('Preview laden...') : t('Preview bekijken')}</button>
            {preview ? <button type="button" disabled={isLocked || isSending} onClick={onSend} className="rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-40">{isSending ? t('Versturen...') : t('Naar deelnemers sturen')}</button> : null}
          </div>
          {messageSent ? <p className="text-sm text-emerald-300">{t('Het bericht staat klaar om verzonden te worden.')}</p> : null}
          {error ? <p className="text-sm text-red-300">{t(error)}</p> : null}
        </div>

        <div className="rounded-2xl border border-white/[0.07] bg-[#111719] p-4 sm:p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-surface-500">{t('Voorbeeld')}</p>
          {preview ? (
            <div className="mt-4 space-y-3 text-sm leading-6 text-surface-200">
              <p className="font-bold text-white">{preview.subject}</p>
              <p>{preview.greeting.replace(':name', t('deelnemer'))}</p>
              {preview.body.split(/\r\n|\r|\n/).filter(Boolean).map((line) => <p key={line}>{line}</p>)}
              <p>{preview.closing}<br />{preview.signature}</p>
              <div className="border-t border-white/[0.07] pt-3 text-xs text-surface-400">
                <p>{t('{count} deelnemers ontvangen dit bericht.', { count: preview.eligible_count })}</p>
                {preview.skipped_count > 0 ? <p>{t('{count} deelnemers worden overgeslagen.', { count: preview.skipped_count })}</p> : null}
              </div>
            </div>
          ) : (
            <p className="mt-4 text-sm leading-6 text-surface-500">{t('Bekijk eerst een preview om te zien hoe de mail eruitziet.')}</p>
          )}
        </div>
      </div>
    </section>
  )
}

function CommunicationHistory({
  t,
  locale,
  communications,
  isLoading,
}: {
  t: (key: string, values?: Record<string, string | number>) => string
  locale: string
  communications: import('@/features/poules/adminApi').PouleAdminCommunication[]
  isLoading: boolean
}) {
  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Communicatie')}</p>
        <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Eerdere berichten')}</h2>
      </div>
      {isLoading ? <p className="mt-4 text-sm text-surface-500">{t('Berichten laden...')}</p> : communications.length === 0 ? <p className="mt-4 text-sm text-surface-500">{t('Nog geen berichten verstuurd.')}</p> : (
        <div className="mt-4 divide-y divide-white/[0.07] border-y border-white/[0.07]">
          {communications.map((communication) => (
            <div key={communication.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-white">{communication.subject}</p>
                <p className="mt-1 text-xs text-surface-500">{formatCommunicationDate(communication.created_at, locale)} · {communication.sent_count} {t('verzonden')}</p>
              </div>
              <span className="rounded-full border border-white/[0.08] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-surface-400">{t(communication.status === 'sent' ? 'Verzonden' : communication.status === 'partial' ? 'Gedeeltelijk verzonden' : 'In behandeling')}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function formatCommunicationDate(value: string | null | undefined, locale: string) {
  if (!value) return '—'
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `poule-admin-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function ImportCard({
  t,
  isLocked,
  lockMessage,
  importPreview,
  isPreviewing,
  isCommitting,
  error,
  onFileSelected,
  onCommit,
}: {
  t: (key: string, values?: Record<string, string | number>) => string
  isLocked: boolean
  lockMessage: string
  importPreview: import('@/features/poules/adminApi').PouleMemberImport | undefined
  isPreviewing: boolean
  isCommitting: boolean
  error: string | null
  onFileSelected: (file: File) => void
  onCommit: () => void
}) {
  return (
    <section className={`rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6 ${isLocked ? 'opacity-75' : ''}`} aria-disabled={isLocked}>
      <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Importeren')}</p>
        <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Ledenbestand beheren')}</h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-400">{t('Upload een CSV-bestand voor een controleerbare preview. Nieuwe e-mailadressen worden niet automatisch als account aangemaakt.')}</p>
        {isLocked ? <p className="mt-3 rounded-xl border border-amber-300/20 bg-amber-300/[0.06] px-3 py-2 text-sm text-amber-100/80">{lockMessage}</p> : null}
      </div>

      <div className="mt-5">
        <div className="rounded-2xl border border-white/[0.07] bg-[#111719] p-4 sm:p-5">
          <label htmlFor="poule-admin-member-import" className="mb-2 block text-sm font-semibold text-surface-200">{t('CSV importeren')}</label>
          <input
            id="poule-admin-member-import"
            type="file"
            accept=".csv,text/csv,text/plain"
            disabled={isLocked || isPreviewing || isCommitting}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) onFileSelected(file)
              event.currentTarget.value = ''
            }}
            className="block w-full text-sm text-surface-400 file:mr-3 file:rounded-lg file:border-0 file:bg-white/[0.08] file:px-3 file:py-2 file:text-xs file:font-bold file:text-surface-100 hover:file:bg-white/[0.12]"
          />
          <p className="mt-2 text-xs leading-5 text-surface-500">{t('Gebruik een kolom met e-mailadres. Voornaam, achternaam en naam zijn optioneel.')}</p>
          {isPreviewing ? <p className="mt-3 text-sm text-surface-400">{t('CSV controleren...')}</p> : null}
          {importPreview ? (
            <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-surface-300 sm:grid-cols-4">
              <ImportStat label={t('Regels')} value={importPreview.total_rows} />
              <ImportStat label={t('Bekende gebruikers')} value={importPreview.matched_rows} />
              <ImportStat label={t('Nieuwe kandidaten')} value={importPreview.candidate_rows} />
              <ImportStat label={t('Fouten')} value={importPreview.error_rows} />
            </div>
          ) : null}
          {importPreview?.status === 'preview' ? <button type="button" disabled={isLocked || isCommitting} onClick={onCommit} className="mt-4 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500 disabled:opacity-40">{isCommitting ? t('Importeren...') : t('Import bevestigen')}</button> : null}
          {importPreview?.status === 'committed' ? <p className="mt-4 text-sm text-emerald-300">{t('{count} aanvragen aangemaakt.', { count: importPreview.pending_members_created })}</p> : null}
          {error ? <p className="mt-3 text-sm text-red-300">{t(error)}</p> : null}
        </div>
      </div>
    </section>
  )
}

function InviteCard({
  t,
  invites,
  isLocked,
  isLoading,
  isCreating,
  isRevoking,
  error,
  onCreate,
  onRevoke,
}: {
  t: (key: string, values?: Record<string, string | number>) => string
  invites: import('@/features/poules/adminApi').PouleAdminInvite[]
  isLocked: boolean
  isLoading: boolean
  isCreating: boolean
  isRevoking: boolean
  error: string | null
  onCreate: () => void
  onRevoke: (inviteId: number) => void
}) {
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [showAll, setShowAll] = useState(false)
  const visibleInvites = showAll ? invites : invites.slice(0, 1)

  async function copyInvite(invite: import('@/features/poules/adminApi').PouleAdminInvite) {
    if (!invite.invite_url) return
    await navigator.clipboard?.writeText(invite.invite_url)
    setCopiedId(invite.id)
    window.setTimeout(() => setCopiedId(null), 1800)
  }

  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Uitnodigingen')}</p>
          <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Mensen uitnodigen')}</h2>
        </div>
        {!isLoading && !invites[0]?.invite_url ? <button type="button" disabled={isLocked || isCreating} onClick={onCreate} className="rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-40">{isCreating ? t('Aanmaken...') : t('Nieuwe invite')}</button> : null}
      </div>
      {isLoading ? <p className="mt-4 text-sm text-surface-500">{t('Uitnodigingen laden...')}</p> : invites.length === 0 ? <p className="mt-4 text-sm text-surface-500">{t('Nog geen actieve uitnodigingen.')}</p> : (
        <div className="mt-4 divide-y divide-white/[0.07] border-y border-white/[0.07]">
          {visibleInvites.map((invite) => <div key={invite.id} className="flex flex-wrap items-center gap-3 py-3"><div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-white" title={invite.invite_url ?? undefined}>{invite.invite_url ?? t('Link niet beschikbaar')}</p><p className="mt-1 text-xs text-surface-500">{invite.used_count} {t('keer gebruikt')}</p></div><button type="button" disabled={isLocked} onClick={() => void copyInvite(invite)} className="rounded-lg border border-white/[0.12] px-3 py-2 text-xs font-bold text-surface-100 transition hover:bg-white/[0.07] disabled:cursor-not-allowed disabled:opacity-40">{copiedId === invite.id ? t('Gekopieerd') : t('Link kopiëren')}</button><button type="button" disabled={isLocked || isRevoking} onClick={() => onRevoke(invite.id)} className="rounded-lg border border-red-300/20 px-3 py-2 text-xs font-bold text-red-200 transition hover:bg-red-300/10 disabled:cursor-not-allowed disabled:opacity-40">{t('Intrekken')}</button></div>)}
        </div>
      )}
      {invites.length > 1 ? <button type="button" disabled={isLocked} onClick={() => setShowAll((value) => !value)} className="mt-3 text-sm font-bold text-surface-300 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-40">{showAll ? t('Minder links tonen') : t('Toon alle links ({count})', { count: invites.length })}</button> : null}
      {error ? <p className="mt-3 text-sm text-red-300">{t(error)}</p> : null}
    </section>
  )
}

function ImportStat({ label, value }: { label: string; value: number }) {
  return <div className="rounded-lg border border-white/[0.06] bg-[#0d1113] px-2.5 py-2"><strong className="block text-base text-white">{value}</strong><span className="mt-0.5 block text-[10px] uppercase tracking-[0.1em] text-surface-500">{label}</span></div>
}

function CorrectionCard({
  t,
  isLocked,
  locale,
  members,
  selectedUserId,
  corrections,
  isLoading,
  isSaving,
  error,
  onSelectUser,
  onSave,
}: {
  t: (key: string, values?: Record<string, string | number>) => string
  isLocked: boolean
  locale: string
  members: import('@/features/poules/adminApi').PouleAdminMember[]
  selectedUserId: number | null
  corrections: import('@/features/poules/adminApi').PouleAdminCorrectionMatch[]
  isLoading: boolean
  isSaving: boolean
  error: string | null
  onSelectUser: (userId: number | null) => void
  onSave: (matchId: number, homePred: number, awayPred: number) => void
}) {
  return (
    <section className={`rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6 ${isLocked ? 'opacity-75' : ''}`} aria-disabled={isLocked}>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Correcties')}</p>
        <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Voorspelling corrigeren')}</h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-surface-400">{t('Pas alleen een nog niet verwerkte voorspelling aan. De oorspronkelijke punten worden niet stilzwijgend herberekend.')}</p>
      </div>
      <div className="mt-5">
        <label htmlFor="poule-admin-correction-user" className="mb-1.5 block text-sm font-semibold text-surface-200">{t('Deelnemer')}</label>
        <div className="w-full max-w-md">
          <Select
            value={selectedUserId == null ? '' : String(selectedUserId)}
            disabled={isLocked}
            onChange={(value) => onSelectUser(value ? Number(value) : null)}
            options={members.map((member) => ({ value: String(member.user_id), label: member.user?.display_name ?? `#${member.user_id}` }))}
            placeholder={t('Kies een deelnemer')}
            ariaLabel={t('Deelnemer')}
          />
        </div>
      </div>
      {selectedUserId && isLoading ? <p className="mt-4 text-sm text-surface-500">{t('Voorspellingen laden...')}</p> : null}
      {selectedUserId && !isLoading && corrections.length === 0 ? <p className="mt-4 text-sm text-surface-500">{t('Geen wedstrijden gevonden die nog kunnen worden gecorrigeerd.')}</p> : null}
      {selectedUserId && corrections.length > 0 ? (
        <div className="mt-4 divide-y divide-white/[0.07] border-y border-white/[0.07]">
          {corrections.map((match) => <CorrectionRow key={match.match_id} match={match} locale={locale} t={t} isLocked={isLocked} isSaving={isSaving} onSave={onSave} />)}
        </div>
      ) : null}
      {error ? <p className="mt-3 text-sm text-red-300">{t(error)}</p> : null}
    </section>
  )
}

function CorrectionRow({
  match,
  locale,
  t,
  isLocked,
  isSaving,
  onSave,
}: {
  match: import('@/features/poules/adminApi').PouleAdminCorrectionMatch
  locale: string
  t: (key: string, values?: Record<string, string | number>) => string
  isLocked: boolean
  isSaving: boolean
  onSave: (matchId: number, homePred: number, awayPred: number) => void
}) {
  const [homePred, setHomePred] = useState(match.home_pred == null ? '' : String(match.home_pred))
  const [awayPred, setAwayPred] = useState(match.away_pred == null ? '' : String(match.away_pred))
  useEffect(() => {
    setHomePred(match.home_pred == null ? '' : String(match.home_pred))
    setAwayPred(match.away_pred == null ? '' : String(match.away_pred))
  }, [match.home_pred, match.away_pred])
  const kickoff = match.kickoff_at ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(match.kickoff_at)) : '—'
  const disabled = isLocked || match.predictions_processed || homePred === '' || awayPred === '' || isSaving

  return (
    <div className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-44 flex-1">
        <p className="text-sm font-bold text-white">{match.home_team ?? '?'} – {match.away_team ?? '?'}</p>
        <p className="mt-1 text-xs text-surface-500">{kickoff}{match.predictions_processed ? ` · ${t('Al verwerkt')}` : ''}</p>
      </div>
      <div className="flex items-center gap-2">
        <input aria-label={t('Thuisvoorspelling')} type="number" min={0} max={99} value={homePred} onChange={(event) => setHomePred(event.target.value)} disabled={isLocked || match.predictions_processed} className="w-16 rounded-lg border border-white/[0.08] bg-[#111719] px-2.5 py-2 text-center text-sm font-bold text-white outline-none focus:border-accent-500/60 disabled:opacity-50" />
        <span className="text-surface-500">–</span>
        <input aria-label={t('Uitvoorspelling')} type="number" min={0} max={99} value={awayPred} onChange={(event) => setAwayPred(event.target.value)} disabled={isLocked || match.predictions_processed} className="w-16 rounded-lg border border-white/[0.08] bg-[#111719] px-2.5 py-2 text-center text-sm font-bold text-white outline-none focus:border-accent-500/60 disabled:opacity-50" />
      </div>
      <button type="button" disabled={disabled} onClick={() => onSave(match.match_id, Number(homePred), Number(awayPred))} className="rounded-lg border border-white/[0.12] px-3 py-2 text-xs font-bold text-surface-100 transition hover:bg-white/[0.07] disabled:cursor-not-allowed disabled:opacity-40">{t('Opslaan')}</button>
    </div>
  )
}

function SettingsCard({
  t,
  settings,
  isLoading,
  isSaving,
  isLocked,
  error,
  onSave,
  isOwner,
  canDelete,
  isDeleting,
  deleteError,
  onDelete,
}: {
  t: (key: string, values?: Record<string, string | number>) => string
  settings: import('@/features/poules/adminApi').PouleAdminSettings | undefined
  isLoading: boolean
  isSaving: boolean
  isLocked: boolean
  error: string | null
  onSave: (payload: Partial<Pick<import('@/features/poules/adminApi').PouleAdminSettings, 'name' | 'description' | 'visibility' | 'requires_approval' | 'prediction_release_mode' | 'show_in_overviews'>>) => void
  isOwner: boolean
  canDelete: boolean
  isDeleting: boolean
  deleteError: string | null
  onDelete: () => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState('private')
  const [requiresApproval, setRequiresApproval] = useState(false)
  const [releaseMode, setReleaseMode] = useState('all_open')
  const [showInOverviews, setShowInOverviews] = useState(false)
  useEffect(() => {
    if (!settings) return
    setName(settings.name)
    setDescription(settings.description ?? '')
    setVisibility(settings.visibility)
    setRequiresApproval(settings.requires_approval)
    setReleaseMode(settings.prediction_release_mode)
    setShowInOverviews(settings.show_in_overviews)
  }, [settings])

  return (
    <section className={`rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6 ${isLocked ? 'opacity-75' : ''}`} aria-disabled={isLocked}>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Instellingen')}</p>
        <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Poule-instellingen')}</h2>
      </div>
      {isLoading ? <p className="mt-4 text-sm text-surface-500">{t('Instellingen laden...')}</p> : (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-semibold text-surface-200">{t('Naam')}<input disabled={isLocked} value={name} onChange={(event) => setName(event.target.value)} className="mt-1.5 w-full rounded-xl border border-white/[0.08] bg-[#111719] px-3 py-2.5 text-sm font-semibold text-white outline-none focus:border-accent-500/60 disabled:cursor-not-allowed disabled:opacity-50" /></label>
          <label className="text-sm font-semibold text-surface-200">{t('Zichtbaarheid')}<span className="mt-1.5 block"><Select disabled={isLocked} value={visibility} onChange={setVisibility} options={[{ value: 'private', label: t('Privé') }, { value: 'public', label: t('Openbaar') }]} placeholder={t('Kies zichtbaarheid')} ariaLabel={t('Zichtbaarheid')} /></span></label>
          <label className="text-sm font-semibold text-surface-200 sm:col-span-2">{t('Omschrijving')}<textarea disabled={isLocked} value={description} onChange={(event) => setDescription(event.target.value)} rows={3} className="mt-1.5 w-full resize-y rounded-xl border border-white/[0.08] bg-[#111719] px-3 py-2.5 text-sm leading-6 text-white outline-none focus:border-accent-500/60 disabled:cursor-not-allowed disabled:opacity-50" /></label>
          <label className="flex items-center gap-2 text-sm font-semibold text-surface-200"><input disabled={isLocked} type="checkbox" checked={requiresApproval} onChange={(event) => setRequiresApproval(event.target.checked)} className="h-4 w-4 accent-accent-500 disabled:opacity-50" />{t('Nieuwe deelnemers eerst goedkeuren')}</label>
          <label className="flex items-center gap-2 text-sm font-semibold text-surface-200"><input disabled={isLocked} type="checkbox" checked={showInOverviews} onChange={(event) => setShowInOverviews(event.target.checked)} className="h-4 w-4 accent-accent-500 disabled:opacity-50" />{t('Tonen in overzichten')}</label>
          <label className="text-sm font-semibold text-surface-200 sm:col-span-2">{t('Voorspellingsmodus')}<span className="mt-1.5 block max-w-md"><Select disabled={isLocked} value={releaseMode} onChange={setReleaseMode} options={[{ value: 'all_open', label: t('Alles open') }, { value: 'stage_by_stage', label: t('Per fase') }]} placeholder={t('Kies voorspellingsmodus')} ariaLabel={t('Voorspellingsmodus')} /></span></label>
          <div className="sm:col-span-2"><button type="button" disabled={isLocked || isSaving || !name.trim()} onClick={() => onSave({ name: name.trim(), description: description.trim() || null, visibility, requires_approval: requiresApproval, prediction_release_mode: releaseMode, show_in_overviews: showInOverviews })} className="rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500 disabled:cursor-not-allowed disabled:opacity-40">{isSaving ? t('Opslaan...') : t('Instellingen opslaan')}</button>{error ? <p className="mt-3 text-sm text-red-300">{t(error)}</p> : null}</div>
          {isOwner ? (
            <div className="sm:col-span-2 rounded-2xl border border-red-300/15 bg-red-300/[0.04] p-4">
              <p className="text-sm font-bold text-white">{t('Poule verwijderen')}</p>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-surface-400">
                {canDelete
                  ? t('Verwijder de poule en alle bijbehorende gegevens definitief.')
                  : t('Je kunt de poule alleen verwijderen als jij de enige geaccepteerde deelnemer bent.')}
              </p>
              {canDelete ? (
                <button type="button" disabled={isDeleting} onClick={onDelete} className="mt-4 inline-flex rounded-xl border border-red-300/25 px-4 py-2.5 text-sm font-bold text-red-100 transition hover:bg-red-300/10 disabled:cursor-not-allowed disabled:opacity-50">
                  {isDeleting ? t('Poule verwijderen...') : t('Poule verwijderen')}
                </button>
              ) : null}
              {deleteError ? <p className="mt-3 text-sm text-red-300">{deleteError}</p> : null}
            </div>
          ) : null}
        </div>
      )}
    </section>
  )
}

function AuditCard({
  t,
  locale,
  events,
  isLoading,
}: {
  t: (key: string, values?: Record<string, string | number>) => string
  locale: string
  events: import('@/features/poules/adminApi').PouleAdminAuditEvent[]
  isLoading: boolean
}) {
  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-5 sm:p-6">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-surface-500">{t('Activiteit')}</p>
        <h2 className="mt-1 text-xl font-black tracking-tight text-white">{t('Beheerlogboek')}</h2>
      </div>
      {isLoading ? <p className="mt-4 text-sm text-surface-500">{t('Activiteit laden...')}</p> : events.length === 0 ? <p className="mt-4 text-sm text-surface-500">{t('Nog geen beheeractiviteiten.')}</p> : (
        <div className="mt-4 divide-y divide-white/[0.07] border-y border-white/[0.07]">
          {events.slice(0, 8).map((event) => <div key={event.id} className="flex flex-wrap items-center justify-between gap-3 py-3"><div><p className="text-sm font-bold text-white">{t(event.action)}</p><p className="mt-1 text-xs text-surface-500">{event.actor?.display_name ?? t('Onbekende beheerder')} · {formatCommunicationDate(event.created_at, locale)}</p></div></div>)}
        </div>
      )}
    </section>
  )
}

function AdminStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-[1.25rem] border border-white/[0.07] bg-[#0d1113] p-4 sm:p-5">
      <p className="text-2xl font-black text-white">{value}</p>
      <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-surface-500">{label}</p>
    </div>
  )
}

function LicenseRequiredDialog({
  t,
  open,
  onClose,
  pouleId,
}: {
  t: (key: string, values?: Record<string, string | number>) => string
  open: boolean
  onClose: () => void
  pouleId: number | null
}) {
  useEffect(() => {
    if (!open) return
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose, open])

  if (!open || pouleId === null) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" role="presentation" onMouseDown={onClose}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="poule-license-required-title"
        className="w-full max-w-lg rounded-t-[1.75rem] border border-white/[0.1] bg-[#0d1113] p-6 shadow-2xl sm:rounded-[1.75rem]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200">{t('Licentie nodig')}</p>
            <h2 id="poule-license-required-title" className="mt-2 text-2xl font-black tracking-tight text-white">{t('Activeer je bedrijfspoule')}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label={t('Sluiten')} className="text-2xl leading-none text-surface-400 transition hover:text-white">×</button>
        </div>
        <p className="mt-4 text-sm leading-6 text-surface-300">{t('Voor deze functie moet je eerst een bedrijfspoulelicentie kiezen en betalen. Daarna kun je direct invite-links maken en de bedrijfsfuncties gebruiken.')}</p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} className="rounded-xl border border-white/[0.12] px-4 py-2.5 text-sm font-bold text-surface-100 transition hover:bg-white/[0.07]">{t('Annuleren')}</button>
          <Link to={'/poules/' + pouleId + '/licentie'} onClick={onClose} className="rounded-xl bg-accent-600 px-4 py-2.5 text-center text-sm font-bold text-white transition hover:bg-accent-500">{t('Licentie kiezen')}</Link>
        </div>
      </section>
    </div>
  )
}

function AdminMemberRow({
  member,
  locale,
  t,
  isBusy,
  isRoleBusy,
  onAction,
  onRoleChange,
}: {
  member: import('@/features/poules/adminApi').PouleAdminMember
  locale: string
  t: (key: string, values?: Record<string, string | number>) => string
  isBusy: boolean
  isRoleBusy: boolean
  onAction: (userId: number, action: 'approve' | 'decline' | 'remove') => Promise<void>
  onRoleChange: (userId: number, role: 'member' | 'admin') => Promise<void>
}) {
  const name = member.user?.display_name ?? `#${member.user_id}`
  const joinedAt = member.joined_at
    ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(member.joined_at))
    : null

  return (
    <div className="flex flex-wrap items-center gap-3 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#182226] text-sm font-bold text-surface-300">
        {member.user?.profile_image ? <img src={member.user.profile_image} alt="" className="h-full w-full object-cover" /> : name.slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-white">{name}</p>
        <p className="mt-0.5 truncate text-xs text-surface-500">
          {member.user?.email ?? t('Geen e-mailadres')}{joinedAt ? ` · ${joinedAt}` : ''}
        </p>
      </div>
      <div className="text-right text-xs text-surface-400">
        <p>{member.total_points} {t('punten')}</p>
        {member.rank ? <p className="mt-0.5">#{member.rank}</p> : null}
      </div>
      {member.status === 'pending' ? (
        <div className="flex gap-2">
          <button type="button" disabled={isBusy} onClick={() => void onAction(member.user_id, 'approve')} className="rounded-lg bg-accent-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-accent-500 disabled:opacity-50">{t('Goedkeuren')}</button>
          <button type="button" disabled={isBusy} onClick={() => void onAction(member.user_id, 'decline')} className="rounded-lg border border-white/[0.12] px-3 py-2 text-xs font-bold text-surface-200 transition hover:bg-white/[0.07] disabled:opacity-50">{t('Afwijzen')}</button>
        </div>
      ) : member.status === 'accepted' && member.can_remove ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={isBusy} onClick={() => void onAction(member.user_id, 'remove')} className="rounded-lg border border-red-300/20 px-3 py-2 text-xs font-bold text-red-200 transition hover:bg-red-300/10 disabled:opacity-50">{t('Verwijderen')}</button>
          {member.can_manage_role ? <button type="button" disabled={isRoleBusy} onClick={() => void onRoleChange(member.user_id, 'admin')} className="rounded-lg border border-white/[0.12] px-3 py-2 text-xs font-bold text-surface-100 transition hover:bg-white/[0.07] disabled:opacity-50">{t('Beheerder maken')}</button> : null}
        </div>
      ) : member.status === 'accepted' && member.can_manage_role ? (
        <button type="button" disabled={isRoleBusy} onClick={() => void onRoleChange(member.user_id, member.role === 'admin' ? 'member' : 'admin')} className="rounded-lg border border-white/[0.12] px-3 py-2 text-xs font-bold text-surface-100 transition hover:bg-white/[0.07] disabled:opacity-50">{member.role === 'admin' ? t('Terugzetten naar deelnemer') : t('Beheerder maken')}</button>
      ) : null}
    </div>
  )
}

function AdminState({ title, body, retry }: { title: string; body: string; retry?: (() => void) | undefined }) {
  const { t } = useTranslation()

  return (
    <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 sm:p-8">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent-300">{t('Poulebeheer')}</p>
      <h1 className="mt-3 text-2xl font-black tracking-tight text-white">{title}</h1>
      <p className="mt-2 max-w-xl text-sm leading-6 text-surface-400">{body}</p>
      {retry ? <button type="button" onClick={retry} className="mt-5 rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500">{t('Opnieuw proberen')}</button> : null}
    </section>
  )
}

function getCompanyPlanLabel(productKey: string | null | undefined, t: (key: string) => string) {
  if (productKey === 'company_pro') return t('Bedrijfspoule Pro')
  if (productKey === 'company_unlimited') return t('Bedrijfspoule Ultimate')
  return t('Bedrijfspoule')
}
